import { ChatPermission } from "@src/gql/graphql";
import { z } from "zod";

const chatActorSchema = z.object({
	id: z.string(),
	email: z.string(),
});

export type ChatActor = z.infer<typeof chatActorSchema>;

const chatCollaboratorSchema = z.object({
	userId: z.string(),
	permission: z.nativeEnum(ChatPermission),
	userEmail: z.string().nullish(),
});

export const chatAccessSchema = z.object({
	ownerId: z.string().nullish(),
	ownerEmail: z.string().nullish(),
	shareKey: z.string().nullish(),
	shareKeyPermission: z.nativeEnum(ChatPermission).nullish(),
	collaboration: z
		.object({
			isShared: z.boolean().nullish(),
			collaborators: z.array(chatCollaboratorSchema).nullish(),
		})
		.nullish(),
});

export type ChatAccess = z.infer<typeof chatAccessSchema>;

function hasChatWritePermission(permission: ChatPermission): boolean {
	return (
		permission === ChatPermission.Write || permission === ChatPermission.Owner
	);
}

function sameEmail(a: string, b: string): boolean {
	return a.toLowerCase() === b.toLowerCase();
}

function isChatOwner(chat: ChatAccess, actor: ChatActor): boolean {
	if (chat.ownerId === actor.id) {
		return true;
	}
	return Boolean(chat.ownerEmail && sameEmail(chat.ownerEmail, actor.email));
}

function findCollaborator(chat: ChatAccess, actor: ChatActor) {
	return chat.collaboration?.collaborators?.find(
		(collaborator) =>
			collaborator.userId === actor.id ||
			Boolean(
				collaborator.userEmail &&
					sameEmail(collaborator.userEmail, actor.email),
			),
	);
}

/**
 * Editable for the owner and for write collaborators.
 * A published read link locks only the session that opened that link.
 * Share sessions stay locked until chat metadata is available so the
 * composer cannot open writable during the load gap.
 */
export function isChatReadOnly(
	chat: unknown,
	actor: ChatActor | null,
	shareKey: string | null,
): boolean {
	if (chat == null) {
		return Boolean(shareKey);
	}
	const parsed = chatAccessSchema.safeParse(chat);
	if (!parsed.success) {
		return true;
	}
	const access = parsed.data;
	if (actor && isChatOwner(access, actor)) {
		return false;
	}
	if (actor) {
		const collaborator = findCollaborator(access, actor);
		if (collaborator) {
			return !hasChatWritePermission(collaborator.permission);
		}
		if (access.ownerId) {
			return true;
		}
	}
	if (shareKey && access.shareKey === shareKey) {
		if (!access.shareKeyPermission) {
			return true;
		}
		return !hasChatWritePermission(access.shareKeyPermission);
	}
	if (!actor && access.shareKey) {
		return true;
	}
	return false;
}
